/* eslint-disable @typescript-eslint/no-explicit-any */
import { useGoogleLogin } from '@react-oauth/google';
import * as axios from "axios"
import { useState, useEffect } from "react"
import { generateRecaptchaToken, PAYWALL_LOCAL_STORAGE_KEY } from './authOperations';
import { postPaywallAuthAppleSignIn, postPaywallAuthGoogleSignIn, postPaywallAuthThirdPartyEndSignUp, postPaywallSignedIn, postPaywallSignedInEnd, postPaywallSignedUp, postPaywallSignedUpEnd } from './paywallApiUrls';
import { AppleSignInResponse, AuthStepsEnum, CustomerAccessData, GoogleSignInResponse, ThirdPartyLoginResponse, PaywallAuthErrorCodes, UserWithEmail, SignInType } from './paywallInterfaces';
/*
  Wrap your app with 'GoogleOAuthProvider' from '@react-oauth/google' and pass the Google client_id to it.
  Then, call loginWithGoogle() and loginWithApple() from the component when appropriate.
*/

/* use Apple’s JavaScript SDK (https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js) */

declare const window: Window & {
  PAYWALL_APPLE_CLIENT_ID: string
  PAYWALL_APPLE_REDIRECT_URI: string
  YitPaywall: {
    addPaywallData?: (key: string, value: any) => boolean
    getPaywallData?: (key: string) => any
    isPaywallDataExists?: (key: string) => boolean
    removePaywallData?: (key: string) => boolean
  }
}

const APPLE_CLIENT_ID = typeof window !== "undefined" && window && window.PAYWALL_APPLE_CLIENT_ID || " ";
const APPLE_REDIRECT_URI = typeof window !== "undefined" && window && window.PAYWALL_APPLE_REDIRECT_URI || " ";

interface UseAuthReturn {
  authError: string | null;
  step: AuthStepsEnum;
  loginWithGoogle: () => void;
  loginWithApple: () => void;
  thirdPartyLogin: (isAgree: boolean) => void;
  sendEmailOrPhoneForLogin: (email: string, agreesTerms?: boolean) => Promise<boolean>;
  submitPassword: (password: string) => Promise<boolean>;
  resetAuth: () => void;
  proceedToEmailStep: () => void;
  user: UserWithEmail | null;
  setAuthError: (error: PaywallAuthErrorCodes) => void;
  setSignInType: (type: SignInType) => void;
  signInType: SignInType;
}

const validateEmail = (email: string) => {
  const emailRegex = /^(([^<>()[\]\\.,;:\s@"+]+(\.[^<>()[\]\\.,;:\s@"+]+)*)|("[^+"]+"))@((\[[0-9]{1,3}(\.[0-9]{1,3}){3}])|(([a-zA-Z0-9\-]+\.)+[a-zA-Z]{2,}))$/;
  return typeof email === "string" && emailRegex.test(email)
}

const validatePhoneNumber = (phone: string) => {
  return typeof phone === "string" && /^05\d{8}$/.test(phone);
}

export const getAuthHookFactory = (type: "signup" | "login", firstStep = AuthStepsEnum.EMAIL) => (): UseAuthReturn => {
  const [authError, setAuthError] = useState<string | null>(null);
  const [step, setStep] = useState<AuthStepsEnum>(firstStep);
  const [email, setEmail] = useState<string | null>(null);
  const [tokenExpireTimeStamp, setTokenExpireTimeStamp] = useState<Date | null>(null)
  const [tokenResponse, setTokenResponse] = useState<string | null>(null)
  const [user, setUser] = useState<UserWithEmail | null>(null);
  const [signInType, setSignInType] = useState<SignInType>(SignInType.otpEmail);
  const decodeJWT = (token: string): any => {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  };

  const createExpireTimeStamp = (expireIn: number) => {
    return new Date(Date.now() + expireIn * 1000)
  }

  const handleExistingCustomer = (data: ThirdPartyLoginResponse) => {
    const customerAccessData: CustomerAccessData = { id: data.id || -1, claims: data.claims || [], displayName: data.displayName || "" };
    handleLoginSuccess(customerAccessData)
  }

  const handleLoginSuccess = (resData: any) => {
    typeof window !== "undefined" && window && window.YitPaywall && typeof window.YitPaywall.addPaywallDataObj === "function" && window.YitPaywall.addPaywallDataObj(PAYWALL_LOCAL_STORAGE_KEY, resData);
    setUser({ email, ...resData });
    setStep(AuthStepsEnum.FINISH);
    setAuthError(null);
    setTokenExpireTimeStamp(null)
    setTokenResponse(null)
    setEmail(null)
  }

  const googleLogin = useGoogleLogin({
    onSuccess: async (response: GoogleSignInResponse) => {
      try {
        const res = await axios.post<{ data: ThirdPartyLoginResponse }>(postPaywallAuthGoogleSignIn(), { thirdPartyToken: response.access_token });
        const resPayload = res.data.data
        const isExistingCustomer = !resPayload.isNewCustomer;

        if (isExistingCustomer) {
          handleExistingCustomer(resPayload)
          return
        }

        if (!isExistingCustomer && type === "login") {
          setAuthError(PaywallAuthErrorCodes.NOT_REGISTERED);
          return;
        }

        const token = resPayload.token;
        setTokenResponse(token);
        setTokenExpireTimeStamp(createExpireTimeStamp(response.expires_in));
        setStep(AuthStepsEnum.CONFIRM_TERMS);
      } catch {
        setAuthError(PaywallAuthErrorCodes.GOOGLE_SIGN_IN_FAILED);
      }
    },
    onError: () => {
      setAuthError(PaywallAuthErrorCodes.GOOGLE_SIGN_IN_FAILED);
    },
  });

  const loginWithGoogle = () => {
    //googleLogin is hook
    googleLogin();
  };

  const loginWithApple = async () => {

    try {
      /* use Apple’s JavaScript SDK (https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js) */
      (window as any).AppleID.auth.init({
        clientId: APPLE_CLIENT_ID,
        redirectURI: APPLE_REDIRECT_URI,
        scope: 'email name',
        usePopup: true,
      });

      const response: AppleSignInResponse = await (window as any).AppleID.auth.signIn();

      const { authorization } = response;
      const { code, id_token } = authorization;

      if (!code || !id_token) {
        throw new Error('Missing authorization code or ID token.');
      }

      const decodedToken = decodeJWT(id_token);
      const expirationTime = decodedToken && decodedToken.exp;

      if (!expirationTime) {
        throw new Error('Missing expiration time in ID token.');
      }

      const res = await axios.post<{ data: ThirdPartyLoginResponse }>(postPaywallAuthAppleSignIn(), { thirdPartyToken: id_token });
      const resData = res.data && res.data.data
      if (!resData) {
        throw new Error();
      }

      const isExistingCustomer = !resData.isNewCustomer;

      if ((window as any).AppleID && (window as any).AppleID.auth && (window as any).AppleID.auth.signInPopup) {
        (window as any).AppleID.auth.signInPopup.close();
      }

      if (isExistingCustomer) {
        handleExistingCustomer(resData)
        return
      }

      if (!isExistingCustomer && type === "login") {
        setAuthError(PaywallAuthErrorCodes.NOT_REGISTERED);
        return;
      }

      setTokenResponse(resData.token);
      setTokenExpireTimeStamp(createExpireTimeStamp(expirationTime));
      setStep(AuthStepsEnum.CONFIRM_TERMS);
    } catch (error) {
      setAuthError(error.message || PaywallAuthErrorCodes.APPLE_SIGN_IN_FAILED);
      return false;
    }
  };

  const thirdPartyLogin = async (isAgree: boolean) => {
    if (tokenExpireTimeStamp && (Date.now() > tokenExpireTimeStamp.getTime())) {
      setAuthError(PaywallAuthErrorCodes.TOKEN_EXPIRED)
      return
    }
    try {
      const response = await axios.post<any>(postPaywallAuthThirdPartyEndSignUp(), { token: tokenResponse, isAgree });
      handleLoginSuccess(response.data.data)
    } catch (error) {
      setAuthError(error.response && error.response.data && error.response.data.message || PaywallAuthErrorCodes.THIRD_PARTY_LOGIN_FAILED);
    }
  }

  const sendEmailOrPhoneForLogin = async (userEmailorPhone: string, agreesTerms?: boolean): Promise<boolean> => {
    try {
      const data: any = { email: userEmailorPhone.trim(), signInType };

      if (type === "signup") {
        if (!agreesTerms) {
          setAuthError(PaywallAuthErrorCodes.SIGNUP_NO_AGREEMENT);
          return false
        }
        data.agreesTerms = agreesTerms
      }

      if (signInType === SignInType.otpEmail && !validateEmail(userEmailorPhone)) {
        setAuthError(PaywallAuthErrorCodes.INVALID_EMAIL);
        return false;
      }

      if (signInType === SignInType.otpPhone && !validatePhoneNumber(userEmailorPhone)) {
        setAuthError(PaywallAuthErrorCodes.INVALID_CELL_PHONE_NUMBER);
        return false;
      }


      const recaptchaToken = await generateRecaptchaToken(type);
      const response: { data: { data: { skipOtp?: boolean; accessData: CustomerAccessData } } } = await axios.post(
        type === "login"
          ? postPaywallSignedIn(recaptchaToken)
          : postPaywallSignedUp(recaptchaToken),
        data
      );

      const { skipOtp, accessData } = response.data.data;
      if (response.data) {
        setEmail(userEmailorPhone);
        if (skipOtp && type === "signup") {
          handleLoginSuccess(accessData);
        } else {
          setStep(AuthStepsEnum.PASSWORD);
        }
        setAuthError(null);
        return true;
      }
      return false;
    } catch (error: any) {
      console.error(error)
      const errorCode = error.response && error.response.data && error.response.data.error && error.response.data.error.errorCode;
      if (errorCode === PaywallAuthErrorCodes.NOT_REGISTERED && signInType === SignInType.otpPhone) {
        setAuthError(PaywallAuthErrorCodes.NOT_REGISTERED_PHONE);
        return false;
      }
      setAuthError(errorCode || PaywallAuthErrorCodes.FAILED_TO_SEND_OTP);
      return false;
    }
  };

  const submitPassword = async (password: string): Promise<boolean> => {
    if (!email) {
      setAuthError(PaywallAuthErrorCodes.INVALID_EMAIL);
      return false;
    }

    try {
      const recaptchaToken = await generateRecaptchaToken(type + "_end");
      const endpoint = type === "signup" ? postPaywallSignedUpEnd(recaptchaToken) : postPaywallSignedInEnd(recaptchaToken);
      const response = await axios.post<any>(endpoint, { email, password, signInType });
      if (response.data) {
        handleLoginSuccess(response.data.data)
        return true;
      }
      return false;
    } catch (error) {
      const errorCode = error.response && error.response.data && error.response.data.error && error.response.data.error.errorCode;
      setAuthError(errorCode || PaywallAuthErrorCodes.AUTHENTICATION_FAILED);
      return false;
    }
  };
  const resetAuth = () => {
    setAuthError(null);
    setStep(AuthStepsEnum.EMAIL);
    setEmail(null);
    setTokenExpireTimeStamp(null)
    setTokenResponse(null)
  };

  useEffect(() => {
    if (!APPLE_CLIENT_ID || APPLE_CLIENT_ID === " " || !APPLE_REDIRECT_URI || APPLE_REDIRECT_URI === " ") {
      console.error("APPLE_CLIENT_ID or APPLE_REDIRECT_URI is not defined or is empty.");
    }
  }, [APPLE_CLIENT_ID, APPLE_REDIRECT_URI]);

  const proceedToEmailStep = () => {
    setStep(AuthStepsEnum.EMAIL);
  };


  return {
    authError,
    step,
    proceedToEmailStep,
    loginWithApple,
    loginWithGoogle,
    thirdPartyLogin,
    sendEmailOrPhoneForLogin,
    submitPassword,
    resetAuth,
    user,
    setAuthError,
    signInType,
    setSignInType
  };
};
